/**
 * Admin client for partner locations — /api/admin-partner-locations.php.
 * Retailer stores / trusted installers (from the trusscore.com maps API) that
 * estimates are shared with. Credentialed (admin session). English-only UI.
 */

import type { NormalizedLocation } from '../config/locationMaps';
import type { ShareKind, MapDatasetName } from '../types/share';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data as { error?: string; message?: string };
    const err = new Error(d.message || d.error || `Request failed (${res.status})`) as Error & { status?: number; data?: unknown };
    err.status = res.status; err.data = data;
    throw err;
  }
  return data as T;
}

export interface PartnerMember { userId: number; email: string; role: string; name: string | null; addedAt: string }

export interface PartnerLocation {
  id: number;
  sfid: string;
  publicId: string | null;
  kind: ShareKind;
  name: string;
  email: string | null;
  emailOverride: string | null;
  phone: string | null;
  street: string | null;
  street2: string | null;
  city: string | null;
  region: string | null;
  regionCode: string | null;
  country: string | null;
  postalCode: string | null;
  url: string | null;
  profileUrl: string | null;
  retailerId: number | null;
  retailerName: string | null;
  retailerActive: boolean | null;
  /** manual = assigned to retailerId by an admin · none = unassigned (quantities only). */
  retailerLinkMode: 'manual' | 'none';
  isActive: boolean;
  lastSyncedAt: string | null;
  memberCount: number;
  shareCount: number;
  activeShareCount: number;
  lastSharedAt: string | null;
  members: PartnerMember[];
  createdAt: string;
  updatedAt: string;
}

export interface PartnerRetailerOption { id: number; name: string; isPartner: boolean; isActive: boolean }

export interface PartnerListResponse {
  locations: PartnerLocation[];
  pagination: { page: number; perPage: number; total: number; pages: number };
  retailers: PartnerRetailerOption[];
  upstreamConfigured: boolean;
}

export type UpstreamLocation = NormalizedLocation & { linkedId: number | null };

export async function fetchPartnerLocations(q: { search?: string; kind?: '' | ShareKind; linked?: '' | '1' | '0'; override?: boolean; page?: number; perPage?: number; retailerId?: number | null }): Promise<PartnerListResponse> {
  const p = new URLSearchParams();
  const { retailerId, ...rest } = q;
  Object.entries(rest).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== false) p.set(k, String(v === true ? 1 : v)); });
  if (retailerId) p.set('retailer_id', String(retailerId));
  const res = await fetch(`${API_BASE}/admin-partner-locations.php?${p.toString()}`, { credentials: 'include' });
  return handle(res);
}

/**
 * Search the trusscore.com map. Give Salesforce ids, a banner (`bg`, e.g. "Home Hardware" —
 * the way to pull a whole chain at once), a city + region, a region, or a country.
 * No free-text name search exists upstream; filter the results client-side.
 */
export async function lookupUpstream(q: { dataset: MapDatasetName; ids?: string; bg?: string; city?: string; region?: string; country?: string; limit?: number }): Promise<{ locations: UpstreamLocation[]; stale?: boolean; notConfigured?: boolean; total?: number }> {
  const p = new URLSearchParams({ lookup: '1' });
  Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') p.set(k, String(v)); });
  const res = await fetch(`${API_BASE}/admin-partner-locations.php?${p.toString()}`, { credentials: 'include' });
  return handle(res);
}

async function post<T>(body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}/admin-partner-locations.php`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return handle(res);
}

export async function createPartnerLocation(input: { sfid: string; dataset: MapDatasetName; id?: string }): Promise<{ location: PartnerLocation; existed: boolean }> {
  return post(input);
}

export interface AssignResult {
  updated: number;
  /** People whose access to the moved stores was carried to the destination node. */
  movedAccess: number;
  /** People who held only these stores and now have nothing (unassign, or keepAccess off). */
  lostAccess: { userId: number; email: string | null }[];
  /** Unassign only: stores with no history (no share, nothing prepared there) were removed from the list entirely. */
  deleted: number;
  locations: PartnerLocation[];
}

/** Bulk move stores to a retailer / sub-retailer (`retailerId`) or unassign them (`null`). */
export async function assignPartnerLocations(ids: number[], retailerId: number | null, keepAccess = true): Promise<AssignResult> {
  return post({ action: 'assign', ids, retailerId, keepAccess });
}

export interface ImportResult {
  created: number[];
  existed: number[];
  failed: { sfid: string; error: string }[];
  assigned: Omit<AssignResult, 'locations'> | null;
  locations: PartnerLocation[];
}

/** Create up to 50 stores from the trusscore.com map and assign them in one go. */
export async function importPartnerLocations(sfids: string[], retailerId: number | null, dataset: MapDatasetName = 'retailers'): Promise<ImportResult> {
  return post({ action: 'import', dataset, sfids, retailerId });
}

export async function patchPartnerLocation(id: number, patch: { emailOverride?: string; retailerId?: number | null; keepAccess?: boolean; isActive?: boolean; resync?: boolean }): Promise<{ location: PartnerLocation }> {
  const res = await fetch(`${API_BASE}/admin-partner-locations.php?id=${id}`, {
    method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });
  return handle(res);
}

export async function updatePartnerMembers(id: number, change: { add?: string[]; remove?: number[] }): Promise<{ members: PartnerMember[]; unknownEmails: string[]; location: PartnerLocation }> {
  const res = await fetch(`${API_BASE}/admin-partner-locations.php?id=${id}&members=1`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(change),
  });
  return handle(res);
}

/** `purge` hard-deletes the store's shares (conversations, events, grants) with it — a testing reset. */
export async function deletePartnerLocation(id: number, purge = false): Promise<{ deleted: boolean; purgedShares: number }> {
  const res = await fetch(`${API_BASE}/admin-partner-locations.php?id=${id}${purge ? '&purge=1' : ''}`, { method: 'DELETE', credentials: 'include' });
  return handle(res);
}
